import React from 'react';
import PropTypes from 'prop-types';
import { withStyles } from '@material-ui/core/styles';
import classNames from 'classnames';
import Chip from '@material-ui/core/Chip';
import Typography from '@material-ui/core/Typography';

const styles = theme => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
  },
  title: {
    marginBottom: theme.spacing(1),
  },
  group: {
    '& > div': {
      marginRight: theme.spacing(0.5),
    },
  },
});

const getLabelAndValue = (option) => {
  let label;
  let value;

  if (typeof option === 'string') {
    label = option;
    value = option;
  } else {
    ({ label, value } = option);
  }

  return {
    label,
    value,
  };
};

/**
 * Displays a group of chip elements. Behaves like a group of either checkboxes or radio buttons, depending on whether
 * or not `multiselect` is true.
 * @param options {Array<String> | Array<{{label: string, value: any}}>>} - Options to display.
 * @param onSelect {function} - Callback function for selection.
 * @param onSelect {function} - Callback function for deletion.
 * @param multiselect {boolean} - Determines whether or not multiple chips can be selected at once.
 * @param title {string} - Title of chip group.
 * @param classes {object}
 * @param className {string}
 * @param value {string | Array<string>}
 * @component
 */
const ChipGroup = ({
  classes,
  className,
  options,
  multiselect,
  value,
  title,
  onDelete,
  onSelect,
  chipIcon,
}) => {
  const selected = new Set(typeof value === 'string' ? [value] : value);

  const onChipClick = option => () => {
    const next = new Set(selected);

    if (multiselect) {
      if (selected.has(option)) {
        next.delete(option);
      } else {
        next.add(option);
      }
    } else if (!selected.has(option)) {
      next.clear();
      next.add(option);
    } else {
      next.clear();
    }

    if (onSelect) {
      if (multiselect) {
        onSelect(Array.from(next));
      } else {
        onSelect(Array.from(next)?.[0] || null);
      }
    }

    if (onDelete) {
      onDelete(option);
    }
  };

  return (
    <div className={classNames(classes.root, className)}>
      {title && (
        <Typography className={classes.title} variant="h3">
          {title}
        </Typography>
      )}
      <div className={classes.group}>
        {options.map((option) => {
          const { label, value: val } = getLabelAndValue(option);

          return (
            <Chip
              key={label}
              label={label}
              color="primary"
              variant={selected.has(val) ? 'default' : 'outlined'}
              onClick={onSelect ? onChipClick(val) : undefined}
              onDelete={onDelete ? onChipClick(val) : undefined}
              icon={chipIcon}
            />
          );
        })}
      </div>
    </div>
  );
};

ChipGroup.propTypes = {
  classes: PropTypes.object.isRequired,
  className: PropTypes.string,
  /**
   * Options to be displayed. Can either be an array of strings or an object shaped like: { label: string, value: any }.
   * The `label` field is displayed on the chip.
   */
  options: PropTypes.array.isRequired,
  /**
   * onSelect callback when a chip is selected or deselected. Passes array of selected values to callback.
   */
  onSelect: PropTypes.func,
  /**
   * Determines whether or not multiple chips can be selected at once.
   */
  multiselect: PropTypes.bool,
  /**
   * The selected value for the chip group. If `multiple` is enabled, `value` can be an array. Otherwise it should be a string.
   */
  value: PropTypes.oneOfType([PropTypes.array, PropTypes.string]).isRequired,
  /**
   * Title for chip group.
   */
  title: PropTypes.string,
  /**
   * Specifies icon to be applied to all chips.
   */
  chipIcon: PropTypes.node,
  /**
   * Specifies action for when x icon is clicked.
   */
  onDelete: PropTypes.func,
};

ChipGroup.defaultProps = {
  className: '',
  multiselect: false,
  onSelect: null,
  title: '',
  chipIcon: null,
  onDelete: null,
};

export default withStyles(styles)(ChipGroup);
